'use client';

import { IProductCurrency } from '@/types/product';
import {
  Flex,
  RangeSlider,
  RangeSliderTrack,
  RangeSliderFilledTrack,
  RangeSliderThumb,
  Text,
  Box,
} from '@chakra-ui/react';
import { FC, useCallback, useMemo, useState, useEffect } from 'react';
import { useDebounceCallback } from 'usehooks-ts';

interface ValueRangeSliderProps {
  range: number[];
  onRangeChange: (range: number[]) => void;
  rangeMin: number;
  rangeMax: number;
  rangeStep?: number;
  currency: IProductCurrency;
}
const ValueRangeSlider: FC<ValueRangeSliderProps> = ({
  range: initialRange,
  onRangeChange,
  rangeMin = 0,
  rangeMax = 10_000,
  rangeStep = 100,
  currency,
}) => {
  const [range, setRange] = useState(initialRange);

  const debouncedChangeCallback = useDebounceCallback(onRangeChange, 300);

  useEffect(() => {
    setRange(initialRange);
  }, [initialRange]);

  const displayValue = useMemo(() => {
    const [min, max] = range;
    if (min === rangeMin && max === rangeMax) return 'Any Value';
    if (min === rangeMin) return `${currency} ${max} or less`;
    if (max === rangeMax) return `${currency} ${min} or more`;

    return `${currency} ${min}-${max}`;
  }, [range, rangeMax, rangeMin, currency]);

  const handleRangeChange = useCallback(
    (newRange: number[]) => {
      const [min, max] = newRange;
      if (max - min < 500) return;

      setRange(newRange);
      debouncedChangeCallback(newRange);
    },
    [debouncedChangeCallback]
  );

  return (
    <Flex flexDir="column" gap="0.25rem">
      <Text
        color="primary.200"
        fontSize="0.875rem"
        fontWeight="300"
        lineHeight="1.25rem"
        textAlign={{ base: 'right', '2xl': 'left' }}
      >
        {displayValue}
      </Text>
      <Box px="0.65rem">
        <RangeSlider
          value={range}
          min={rangeMin}
          max={rangeMax}
          step={rangeStep}
          onChange={handleRangeChange}
        >
          <RangeSliderTrack>
            <RangeSliderFilledTrack />
          </RangeSliderTrack>
          <RangeSliderThumb index={0} />
          <RangeSliderThumb index={1} />
        </RangeSlider>
      </Box>
    </Flex>
  );
};

export default ValueRangeSlider;
